import React from 'react';
import AgentSelector from '../AgentSelector';
import './ConnectorConfigEvaluation.css';

export interface ConnectorAgentSelection {
  type: 'coordinator' | 'worker' | 'meta';
  index?: number;
  metaPromptKey?: string;
}

export interface ConnectorAgentDisplayData {
  name: string;
  prompt: string;
  model: string;
  temperature: number;
  description: string;
  reminder_prompt?: string;
  mode?: string;
}

export interface ConnectorConfigEvaluationViewProps {
  evaluationReady: boolean;
  selectedInitialAgent: ConnectorAgentSelection;
  selectedOptimizedAgent: ConnectorAgentSelection;
  onInitialAgentClick: (
    agentType: 'coordinator' | 'worker' | 'meta',
    agentIndex?: number,
    metaPromptKey?: string
  ) => void;
  onOptimizedAgentClick: (
    agentType: 'coordinator' | 'worker' | 'meta',
    agentIndex?: number,
    metaPromptKey?: string
  ) => void;
  initialWorkersCount: number;
  optimizedWorkersCount: number;
  optimizedIterationIndex?: number;
  initialData: ConnectorAgentDisplayData | null;
  initialPromptLive?: boolean;
  optimizedData: ConnectorAgentDisplayData | null;
  optimizedResultsCount: number;
  currentStepIndex: number;
  currentStepSeq: number;
  isAnimating: boolean;
  onPrevStep: () => void;
  onNextStep: () => void;
  onSelectStep: (index: number) => void;
  onExportStep?: () => void;
}

function MetaChips({
  data,
  showMode,
}: {
  data: ConnectorAgentDisplayData;
  showMode?: boolean;
}) {
  const isMeta = data.model === 'N/A';
  return (
    <div className="connector-config-eval__meta">
      {!isMeta && (
        <>
          <span className="connector-config-eval__chip">
            <strong>Model</strong> {data.model}
          </span>
          <span className="connector-config-eval__chip">
            <strong>Temp</strong> {data.temperature.toFixed(2)}
          </span>
        </>
      )}
      {showMode && data.mode && (
        <span className="connector-config-eval__chip">
          <strong>Mode</strong> {data.mode}
        </span>
      )}
    </div>
  );
}

function AgentDetails({
  data,
  showReminder,
}: {
  data: ConnectorAgentDisplayData;
  showReminder: boolean;
}) {
  const hasReminder =
    showReminder && (data.reminder_prompt ?? '').trim().length > 0;
  const hasDescription =
    data.description.trim().length > 0 &&
    !data.description.startsWith('Meta prompt configuration:');

  return (
    <>
      {hasReminder && (
        <details className="connector-config-eval__details">
          <summary>Reminder prompt</summary>
          <div className="connector-config-eval__details-body">
            {data.reminder_prompt}
          </div>
        </details>
      )}
      {hasDescription && (
        <details className="connector-config-eval__details">
          <summary>Description</summary>
          <div className="connector-config-eval__details-body">
            {data.description}
          </div>
        </details>
      )}
    </>
  );
}

function EvaluationEmpty({
  title,
  text,
}: {
  title: string;
  text: string;
}) {
  return (
    <div className="connector-config-eval__empty">
      <i className="fas fa-hourglass-half" aria-hidden />
      <div className="connector-config-eval__empty-title">{title}</div>
      <p className="connector-config-eval__empty-text">{text}</p>
    </div>
  );
}

const ConnectorConfigEvaluationView: React.FC<ConnectorConfigEvaluationViewProps> = ({
  evaluationReady,
  selectedInitialAgent,
  selectedOptimizedAgent,
  onInitialAgentClick,
  onOptimizedAgentClick,
  initialWorkersCount,
  optimizedWorkersCount,
  optimizedIterationIndex,
  initialData,
  initialPromptLive,
  optimizedData,
  optimizedResultsCount,
  currentStepIndex,
  currentStepSeq,
  isAnimating,
  onPrevStep,
  onNextStep,
  onSelectStep,
  onExportStep,
}) => {
  const initialIsMeta = selectedInitialAgent.type === 'meta';
  const optimizedIsMeta = selectedOptimizedAgent.type === 'meta';

  return (
    <section className="connector-config-eval" id="visualization">
      <header className="connector-config-eval__header">
        <span className="connector-config-eval__eyebrow">Compare</span>
        <h2 className="connector-config-eval__title">Configuration evaluation</h2>
        <p className="connector-config-eval__subtitle">
          {evaluationReady
            ? 'Select agents independently in each column to compare baseline and optimized configs.'
            : 'Run optimization or load a configuration from history to compare baseline and optimized configs.'}
        </p>
      </header>

      <div className="connector-config-eval__split-head">
        <div className="connector-config-eval__split-head-cell connector-config-eval__split-head-cell--initial">
          <div className="connector-config-eval__split-head-top">
            <div>
              <span className="connector-config-eval__col-label">Initial</span>
              <p className="connector-config-eval__col-hint">Baseline configuration</p>
            </div>
            {initialData && (
              <MetaChips
                data={initialData}
                showMode={selectedInitialAgent.type === 'worker'}
              />
            )}
          </div>
          {evaluationReady && (
            <div className="connector-config-eval__col-picker">
              <AgentSelector
                isConnector
                variant="initial"
                onAgentClick={onInitialAgentClick}
                selectedAgent={selectedInitialAgent}
                workersCount={initialWorkersCount}
              />
            </div>
          )}
        </div>

        <div className="connector-config-eval__split-head-cell connector-config-eval__split-head-cell--optimized">
          <div className="connector-config-eval__split-head-top">
            <div>
              <span className="connector-config-eval__col-label connector-config-eval__col-label--optimized">
                Optimized
                {optimizedResultsCount > 0 ? ` · step ${currentStepSeq}` : ''}
              </span>
              <p className="connector-config-eval__col-hint">
                {optimizedResultsCount > 0
                  ? 'Configuration after optimization'
                  : 'Available after the first step'}
              </p>
            </div>
            {optimizedData && (
              <MetaChips
                data={optimizedData}
                showMode={selectedOptimizedAgent.type === 'worker'}
              />
            )}
          </div>

          {optimizedResultsCount > 0 && (
            <div className="connector-config-eval__optimized-tools">
              <div className="connector-config-eval__step-nav">
                <button
                  type="button"
                  className="connector-config-eval__step-btn"
                  onClick={onPrevStep}
                  disabled={isAnimating || optimizedResultsCount <= 1}
                  aria-label="Previous step"
                >
                  <i className="fas fa-chevron-left" aria-hidden />
                </button>
                {optimizedResultsCount > 1 ? (
                  <select
                    className="connector-config-eval__step-select"
                    value={currentStepIndex}
                    disabled={isAnimating}
                    onChange={(e) => onSelectStep(Number(e.target.value))}
                    aria-label="Optimization step"
                  >
                    {Array.from({ length: optimizedResultsCount }, (_, index) => {
                      const seq = index + 1;
                      return (
                        <option key={index} value={index}>
                          Step {seq}
                        </option>
                      );
                    })}
                  </select>
                ) : (
                  <span className="connector-config-eval__step-label">Step {currentStepSeq}</span>
                )}
                <button
                  type="button"
                  className="connector-config-eval__step-btn"
                  onClick={onNextStep}
                  disabled={isAnimating || optimizedResultsCount <= 1}
                  aria-label="Next step"
                >
                  <i className="fas fa-chevron-right" aria-hidden />
                </button>
              </div>
              {onExportStep && (
                <button
                  type="button"
                  className="connector-config-eval__export-btn"
                  onClick={onExportStep}
                  title={`Export step ${currentStepSeq} to Integration Configurator`}
                >
                  <i className="fas fa-upload" aria-hidden />
                  Export
                </button>
              )}
            </div>
          )}

          {optimizedResultsCount > 0 && (
            <div className="connector-config-eval__col-picker">
              <AgentSelector
                isConnector
                variant="optimized"
                iterationIndex={optimizedIterationIndex}
                onAgentClick={onOptimizedAgentClick}
                selectedAgent={selectedOptimizedAgent}
                workersCount={optimizedWorkersCount}
              />
            </div>
          )}
        </div>
      </div>

      <div className="connector-config-eval__compare">
        <div className="connector-config-eval__col connector-config-eval__col--initial">
          {initialData ? (
            <>
              <div className="connector-config-eval__agent-name">{initialData.name}</div>
              <div className="connector-config-eval__prompt-block">
                <div className="connector-config-eval__field-label">Prompt</div>
                <pre
                  className={`connector-config-eval__prompt-text${
                    initialPromptLive ? ' connector-config-eval__prompt-text--live' : ''
                  }`}
                >
                  {initialData.prompt}
                </pre>
              </div>
              {!initialIsMeta && (
                <AgentDetails data={initialData} showReminder={!initialIsMeta} />
              )}
            </>
          ) : (
            <EvaluationEmpty
              title="No baseline yet"
              text="Start optimization or load a run from history to capture the initial configuration."
            />
          )}
        </div>

        <div className="connector-config-eval__col connector-config-eval__col--optimized">
          {optimizedData ? (
            <>
              <div className="connector-config-eval__agent-name">{optimizedData.name}</div>
              <div className="connector-config-eval__prompt-block">
                <div className="connector-config-eval__field-label">Prompt</div>
                <pre className="connector-config-eval__prompt-text">{optimizedData.prompt}</pre>
              </div>
              {!optimizedIsMeta && (
                <AgentDetails data={optimizedData} showReminder={!optimizedIsMeta} />
              )}
            </>
          ) : (
            <EvaluationEmpty
              title="Optimization not started"
              text="Optimized configs appear here after the first step completes."
            />
          )}
        </div>
      </div>
    </section>
  );
};

export default ConnectorConfigEvaluationView;
